import PageShell from "../components/PageShell";

const Section = ({ title, children, testId }) => (
  <section data-testid={testId} className="mb-10">
    <h2 className="font-cinzel text-amber-200 text-xl sm:text-2xl tracking-wide mb-4">
      {title}
    </h2>
    <div className="text-stone-300 text-[15px] leading-relaxed space-y-4">
      {children}
    </div>
  </section>
);

const Terms = () => {
  return (
    <PageShell testId="terms-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 md:py-24">
        <p className="text-amber-300/80 text-xs tracking-[0.25em] uppercase mb-4">
          Legal · IRL Stats
        </p>
        <h1
          data-testid="terms-heading"
          className="font-cinzel text-4xl sm:text-5xl text-amber-50 leading-tight mb-3"
        >
          Terms of Service
        </h1>
        <p className="text-stone-500 text-sm mb-12">
          Effective Date: May 21, 2026
        </p>

        <div className="mb-10 p-5 rounded-lg border border-amber-500/20 bg-amber-400/5 text-stone-300 text-sm">
          These Terms of Service ("Terms") form a binding agreement between you
          and <strong>The Legacy Labs LLC</strong>, doing business as{" "}
          <strong>IRL Stats</strong> ("IRL Stats," "we," "us," or "our"). By
          downloading, accessing, or using the IRL Stats mobile application and
          related services (the "Service"), you agree to be bound by these
          Terms. If you do not agree, do not use the Service.
        </div>

        <Section title="1. Eligibility" testId="terms-section-eligibility">
          <p>
            You must be at least <strong>13 years old</strong> to use the
            Service. If you are under <strong>18 years of age</strong>, you may
            use the Service only with the involvement, consent, and supervision
            of a parent or legal guardian who agrees to be bound by these Terms
            on your behalf. By using the Service, you represent that you meet
            these requirements.
          </p>
        </Section>

        <Section title="2. Your Account" testId="terms-section-account">
          <p>
            You are responsible for maintaining the confidentiality of your
            account credentials and for all activity that occurs under your
            account. Notify us immediately of any unauthorized use. We may
            suspend or terminate accounts that violate these Terms or are used
            in a manner that harms the Service or other users.
          </p>
        </Section>

        <Section title="3. Fitness Risk Disclaimer" testId="terms-section-fitness-risk">
          <p>
            <strong>
              Exercise involves inherent risk of injury, illness, or death.
            </strong>{" "}
            You acknowledge that physical activity, weightlifting, cardio,
            outdoor workouts, and related activities carry these risks. You
            agree that you participate in any exercise activity{" "}
            <strong>voluntarily and at your own risk</strong>.
          </p>
          <p>
            You should consult a qualified physician or healthcare professional
            before beginning any exercise program, especially if you have any
            medical condition, are pregnant, are recovering from injury, or are
            taking medication. Stop exercising immediately and seek medical
            attention if you experience pain, dizziness, shortness of breath,
            or any other warning sign.
          </p>
          <p>
            You are solely responsible for your form, technique, equipment,
            environment, and decisions made during training. IRL Stats is not
            responsible for any injury, harm, or loss resulting from your use
            of the Service.
          </p>
        </Section>

        <Section title="4. Not Medical Advice" testId="terms-section-not-medical">
          <p>
            The Service is for informational, motivational, and tracking
            purposes only. Content within the Service (including XP,
            achievements, workout templates, and suggestions) is{" "}
            <strong>not medical, nutritional, or professional advice</strong>{" "}
            and is not a substitute for advice from a qualified healthcare
            provider. Do not disregard professional advice or delay seeking it
            because of anything you encountered in the Service.
          </p>
        </Section>

        <Section title="5. Subscriptions and Payment" testId="terms-section-subscriptions">
          <p>
            Certain features of the Service may require a paid subscription.
            Subscriptions are billed through the Apple App Store and are
            governed by Apple's terms in addition to these Terms.
          </p>
          <ul className="list-disc pl-6 space-y-2 text-stone-300">
            <li>
              Subscriptions automatically renew at the end of each billing
              period unless canceled at least 24 hours before the end of the
              current period.
            </li>
            <li>
              You can manage and cancel subscriptions in your Apple ID account
              settings. Cancellations take effect at the end of the current
              billing period.
            </li>
            <li>
              Except where required by law, payments are non-refundable. Refund
              requests must be submitted directly to Apple.
            </li>
            <li>
              Prices and features may change. We will provide notice of material
              changes where required.
            </li>
          </ul>
        </Section>

        <Section title="6. GPS and Location Features" testId="terms-section-gps">
          <p>
            Certain features may use GPS or location data to track routes,
            distance, or location-based activities. Location features are
            optional and require your permission. You may disable location
            access at any time in your device settings. Accuracy of GPS data
            depends on your device, signal availability, and environmental
            conditions. IRL Stats does not guarantee the accuracy of any
            location-based measurement.
          </p>
        </Section>

        <Section title="7. Co-op and Social Features" testId="terms-section-coop">
          <p>
            The Service offers a local Co-op (gym partner) mode that allows
            two people to share a single device and switch between them while
            logging a workout. Each person's data is saved separately to their
            own profile or guest record.
          </p>
          <p>
            You are solely responsible for your interactions with other users
            and for any content you share. You agree not to harass, threaten,
            impersonate, or harm other users; not to share content that is
            illegal, abusive, or infringes others' rights; and not to use
            co-op features in any unsafe manner. We may remove content and
            suspend accounts that violate these standards, in our sole
            discretion.
          </p>
        </Section>

        <Section title="8. Acceptable Use" testId="terms-section-acceptable-use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-2 text-stone-300">
            <li>
              Reverse engineer, decompile, or attempt to extract source code
              from the Service;
            </li>
            <li>
              Use bots, scripts, or automated means to interact with the
              Service or fabricate XP, achievements, or stats;
            </li>
            <li>
              Interfere with or disrupt the Service or its servers;
            </li>
            <li>Use the Service to violate any law or regulation.</li>
          </ul>
        </Section>

        <Section title="9. Intellectual Property" testId="terms-section-ip">
          <p>
            The Service, including all software, design, text, graphics, logos,
            and the "IRL Stats" name and brand, is owned by The Legacy Labs LLC
            and protected by intellectual property laws. We grant you a
            limited, non-exclusive, non-transferable, revocable license to use
            the Service for your personal, non-commercial use.
          </p>
        </Section>

        <Section title="10. User Content" testId="terms-section-user-content">
          <p>
            You retain ownership of content you submit (such as workout notes,
            usernames, and profile data). By submitting content, you grant us a
            worldwide, royalty-free license to host, store, reproduce, and
            display that content solely as necessary to operate and provide the
            Service.
          </p>
        </Section>

        <Section title="11. Termination" testId="terms-section-termination">
          <p>
            You may stop using the Service at any time and request account
            deletion by emailing{" "}
            <a
              href="mailto:irlstatsapp@gmail.com"
              className="text-amber-300 underline-offset-2 hover:underline"
            >
              irlstatsapp@gmail.com
            </a>
            . We may suspend or terminate your access at any time for any
            reason, including violation of these Terms.
          </p>
        </Section>

        <Section title="12. Disclaimer of Warranties" testId="terms-section-disclaimers">
          <p>
            THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. TO
            THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES,
            EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY,
            FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND ANY
            WARRANTY ARISING FROM COURSE OF DEALING OR USAGE OF TRADE. WE DO
            NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR
            SECURE.
          </p>
        </Section>

        <Section title="13. Limitation of Liability" testId="terms-section-liability">
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL THE LEGACY
            LABS LLC, ITS AFFILIATES, OFFICERS, EMPLOYEES, OR AGENTS BE LIABLE
            FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
            DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF
            OR RELATING TO YOUR USE OF THE SERVICE, EVEN IF WE HAVE BEEN
            ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p>
            OUR TOTAL LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING TO
            THE SERVICE WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID
            TO US IN THE TWELVE (12) MONTHS BEFORE THE EVENT GIVING RISE TO THE
            CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS (USD $100).
          </p>
        </Section>

        <Section title="14. Indemnification" testId="terms-section-indemnification">
          <p>
            You agree to defend, indemnify, and hold harmless The Legacy Labs
            LLC and its affiliates, officers, employees, and agents from and
            against any claims, liabilities, damages, losses, and expenses
            (including reasonable attorneys' fees) arising out of or related
            to: (a) your use of the Service; (b) your violation of these Terms;
            (c) your violation of any law or third-party right; or (d) any
            content you submit.
          </p>
        </Section>

        <Section title="15. Governing Law" testId="terms-section-governing-law">
          <p>
            These Terms are governed by and construed in accordance with the
            laws of the <strong>State of Utah</strong>, without regard to its
            conflict of laws principles.
          </p>
        </Section>

        <Section title="16. Binding Arbitration" testId="terms-section-arbitration">
          <p>
            <strong>
              Please read this section carefully — it affects your legal
              rights.
            </strong>
          </p>
          <p>
            Any dispute, claim, or controversy arising out of or relating to
            these Terms or the Service will be resolved by{" "}
            <strong>binding individual arbitration</strong> administered in
            accordance with the rules of a recognized arbitration provider in
            the State of Utah. The arbitration will take place in Utah, unless
            you and we agree otherwise. Judgment on the award may be entered in
            any court of competent jurisdiction.
          </p>
          <p>
            <strong>Class action waiver:</strong> You and IRL Stats agree that
            disputes will be brought only in an individual capacity, and not as
            a plaintiff or class member in any purported class or
            representative proceeding.
          </p>
          <p>
            Notwithstanding the foregoing, either party may bring an individual
            action in small-claims court, and either party may seek injunctive
            relief in court for infringement of intellectual property rights.
          </p>
        </Section>

        <Section title="17. Prevailing Party Attorneys' Fees" testId="terms-section-attorneys-fees">
          <p>
            In any action or proceeding to enforce or interpret these Terms,
            the <strong>prevailing party</strong> shall be entitled to recover
            its reasonable attorneys' fees, costs, and expenses, in addition to
            any other relief to which it may be entitled.
          </p>
        </Section>

        <Section title="18. Changes to These Terms" testId="terms-section-changes">
          <p>
            We may update these Terms from time to time. The updated Terms will
            be posted with a new effective date. Material changes will be
            communicated through the Service or by email. Your continued use
            of the Service after the changes take effect constitutes your
            acceptance.
          </p>
        </Section>

        <Section title="19. Miscellaneous" testId="terms-section-misc">
          <p>
            If any provision of these Terms is held invalid or unenforceable,
            the remaining provisions will remain in full force and effect. Our
            failure to enforce any right or provision is not a waiver of that
            right. These Terms, together with our Privacy Policy, constitute
            the entire agreement between you and us regarding the Service.
          </p>
        </Section>

        <Section title="20. Contact" testId="terms-section-contact">
          <p>
            The Legacy Labs LLC, dba IRL Stats
            <br />
            Email:{" "}
            <a
              href="mailto:irlstatsapp@gmail.com"
              className="text-amber-300 underline-offset-2 hover:underline"
            >
              irlstatsapp@gmail.com
            </a>
          </p>
        </Section>
      </div>
    </PageShell>
  );
};

export default Terms;
